import { gql, useQuery } from '@apollo/client'
import {
  Ledger,
  Query,
  Restriction,
  RetailerAccount,
} from '@promoboxx/graphql-gateway-types'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

export const GET_RETAILER_WALLET = gql`
  query getRetailerWalletHook($business_id: ID!) {
    get_wallet_by_brand_or_business(business_id: $business_id) {
      id
      balance
      created_at

      ledgers {
        id
        balance
        expiration_date

        restrictions {
          key
          filter_value
        }
      }
    }
  }
`

interface FindLedgerOptions {
  excludeExpired?: boolean
  excludeEmpty?: boolean
}

function useRetailerAccountWallet() {
  const currentRetailerAccount: RetailerAccount = useSelector(
    currentRetailerAccountSelector,
  )

  const { data, refetch: refetchWallet } = useQuery<Query>(
    GET_RETAILER_WALLET,
    {
      variables: {
        business_id: currentRetailerAccount.business_id,
      },
    },
  )

  const ledgers = useMemo(() => {
    return data?.get_wallet_by_brand_or_business.ledgers || []
  }, [data?.get_wallet_by_brand_or_business.ledgers])

  const findLedgersForBrand = useCallback(
    (brandId: Restriction['filter_value'], options?: FindLedgerOptions) => {
      return ledgers.filter((x) => {
        let doesPassChecks = true

        if (options?.excludeExpired && isLedgerExpired(x)) {
          doesPassChecks = false
        }

        if (options?.excludeEmpty && x.balance === '0') {
          doesPassChecks = false
        }

        return (
          x.restrictions[0]?.key === 'brand' &&
          x.restrictions[0]?.filter_value === brandId &&
          doesPassChecks
        )
      })
    },
    [ledgers],
  )

  const findLedgersForPaidAd = useCallback(
    (campaignId: Restriction['filter_value'], options?: FindLedgerOptions) => {
      return ledgers.filter((x) => {
        let doesPassChecks = true

        if (options?.excludeExpired && isLedgerExpired(x)) {
          doesPassChecks = false
        }

        if (options?.excludeEmpty && x.balance === '0') {
          doesPassChecks = false
        }

        return (
          x.restrictions[0]?.key === 'campaign' &&
          x.restrictions[0]?.filter_value === campaignId &&
          doesPassChecks
        )
      })
    },
    [ledgers],
  )

  const findBrandLedgers = useCallback(
    (options?: FindLedgerOptions) => {
      return ledgers.filter((x) => {
        let doesPassChecks = true

        if (options?.excludeExpired && isLedgerExpired(x)) {
          doesPassChecks = false
        }

        if (options?.excludeEmpty && x.balance === '0') {
          doesPassChecks = false
        }

        return x.restrictions[0]?.key === 'brand' && doesPassChecks
      })
    },
    [ledgers],
  )

  const findPaidAdLedgers = useCallback(
    (options?: FindLedgerOptions) => {
      return ledgers.filter((x) => {
        let doesPassChecks = true

        if (options?.excludeExpired && isLedgerExpired(x)) {
          doesPassChecks = false
        }

        if (options?.excludeEmpty && x.balance === '0') {
          doesPassChecks = false
        }

        return x.restrictions[0]?.key === 'campaign' && doesPassChecks
      })
    },
    [ledgers],
  )

  const findSelfFundedLedgers = useCallback(
    (options?: FindLedgerOptions) => {
      return ledgers.filter((x) => {
        let doesPassChecks = true

        if (options?.excludeExpired && isLedgerExpired(x)) {
          doesPassChecks = false
        }

        if (options?.excludeEmpty && x.balance === '0') {
          doesPassChecks = false
        }

        return (
          x.restrictions[0]?.key === 'business' &&
          x.restrictions[0]?.filter_value ===
            currentRetailerAccount.business_id &&
          doesPassChecks
        )
      })
    },
    [currentRetailerAccount.business_id, ledgers],
  )

  // Warning: Here be potential dragons. All the other `find...` helpers here
  // are pretty exact, finding exactly what they say they are finding. This is
  // closer to "product logic", aka its arguments and implementation are liable
  // to change.
  const findLedgersApplicableToPaidAd = useCallback(
    (brandId: Restriction['filter_value'], options?: FindLedgerOptions) => {
      return findLedgersForBrand(brandId, options).concat(
        findSelfFundedLedgers(),
      )
    },
    [findLedgersForBrand, findSelfFundedLedgers],
  )

  return {
    ledgers,

    findLedgersForBrand,
    findLedgersForPaidAd,
    findBrandLedgers,
    findPaidAdLedgers,
    findSelfFundedLedgers,

    findLedgersApplicableToPaidAd,

    refetchWallet,
  }
}

function isLedgerExpired(ledger: Ledger) {
  return (
    ledger.expiration_date != null &&
    !ledger.expiration_date.startsWith('0001') &&
    new Date(ledger.expiration_date) < new Date()
  )
}

export function sortLedgersByClosestExpiration(
  potentiallyReadOnlyLedgers: Ledger[],
) {
  const sortableLedgers = [...potentiallyReadOnlyLedgers]

  // This is a little weird, goal is to sort:
  // - closest expiring ledgers first
  // - non-expiring go to the bottom
  sortableLedgers.sort((a, b) => {
    if (!a.expiration_date || !b.expiration_date) {
      return -1
    }

    const aExpirationDistance =
      new Date().valueOf() - new Date(a.expiration_date).valueOf()
    const bExpirationDistance =
      new Date().valueOf() - new Date(b.expiration_date).valueOf()

    return bExpirationDistance - aExpirationDistance
  })

  return sortableLedgers
}

export default useRetailerAccountWallet
